/*
  <shintop-guide-finder> — the Guides index's search and sort.

  WHAT IT IS AND IS NOT. The topic pills are NOT part of this element's job: they are real
  `/blogs/guides/tagged/<topic>` links, indexable and shareable, and they keep working with
  JavaScript off. This handles only the two controls that have no URL worth having — a search
  box and a sort order — over the guides already in the document.

  THAT IS HONEST ONLY WHILE THE WHOLE CORPUS IS ON THE PAGE. The section paginates at 24 and
  the blog holds 9, so every guide is here. The Liquid gates both controls on `paginate.pages
  < 2`, so on the day a 25th guide is published they stop rendering rather than start lying —
  a search covering page 1 while reporting the blog would answer "no guides match" for a guide
  that exists. That is the moment to move search to `/search?type=article`.

  PROGRESSIVE ENHANCEMENT IS THE WHOLE DESIGN. The controls are rendered `hidden` by Liquid and
  un-hidden here, so a browser that never runs this file shows the pills and the full list and
  nothing that does not work. Nothing in here composes a sentence: the tally and the no-match
  line are markup, and the only thing JS writes is a number into a `{{ count }}` slot that
  Liquid put there.
*/
class ShintopGuideFinder extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-guide]'));
    if (!this.items.length) return;

    this.list = this.items[0].parentElement;
    this.tally = this.querySelector('[data-finder-tally]');
    this.none = this.querySelector('[data-finder-none]');
    this.searchWrap = this.querySelector('[data-finder-search]');
    this.input = this.querySelector('[data-finder-input]');
    this.clear = this.querySelector('[data-finder-clear]');
    this.sortWrap = this.querySelector('[data-finder-sortwrap]');
    this.sort = this.querySelector('[data-finder-sort]');

    /*
      THE PLURAL FORMS ARE READ OFF THE RENDERED TALLY, not rebuilt here. Liquid printed the
      real count into it with the locale's own pluralisation; this stores the two templates so
      the element can re-print any number in the same words, in whatever language the store is
      running, without a string ever being written in JavaScript. `data-*` carries them.
    */
    if (this.tally) this.tallyTemplates = this.tally.dataset;

    if (this.searchWrap) {
      this.searchWrap.hidden = false;
      this.input.addEventListener('input', this.onInput.bind(this));
      // A search input's native clear (the × in WebKit) fires `search`, not `input`.
      this.input.addEventListener('search', this.onInput.bind(this));
      this.clear.addEventListener('click', () => {
        this.input.value = '';
        this.input.focus();
        this.apply();
      });
    }

    if (this.sortWrap) {
      this.sortWrap.hidden = false;
      this.sort.addEventListener('change', this.apply.bind(this));
    }

    /*
      `is-managed` says "this file ran", which is what tells the stylesheet which of the two
      orphan rules is the live one. See markTail().
    */
    if (this.list) this.list.classList.add('is-managed');
    this.markTail();
  }

  onInput() {
    /*
      DEBOUNCED, because `role="status"` on the tally means every keystroke would otherwise
      queue an announcement and a screen reader would read the count nine times while a word
      is typed. 200ms is below the threshold where filtering feels laggy and above the gap
      between two keystrokes of ordinary typing.
    */
    clearTimeout(this.pending);
    this.pending = setTimeout(this.apply.bind(this), 200);
  }

  apply() {
    const query = this.input ? this.input.value.trim().toLowerCase() : '';
    const words = query ? query.split(/\s+/) : [];

    /*
      EVERY word must match, anywhere in the haystack Liquid built (title, topic, summary and
      the descriptive tags). AND rather than OR: "red bean" should narrow to the red-bean
      guides, not widen to everything containing "red" — and with nine guides an OR search
      returns most of the blog for most queries, which reads as broken.
    */
    let shown = 0;
    this.items.forEach((item) => {
      const hay = item.dataset.terms || '';
      const hit = words.every((w) => hay.indexOf(w) !== -1);
      item.hidden = !hit;
      if (hit) shown += 1;
    });

    if (this.clear) this.clear.hidden = query === '';
    if (this.none) this.none.hidden = shown !== 0;

    this.reorder();
    this.print(shown, query !== '');
  }

  reorder() {
    const mode = this.sort ? this.sort.value : 'newest';
    const rows = this.items.slice();

    /*
      `newest` IS SOURCE ORDER, and source order is `published_at` descending — Liquid's own,
      not a date parsed out of the DOM. r8/r26 settled that the date PRINTED is
      `custom.updated_at` while the date SORTED is `published_at`, so reading the printed date
      back off the row and sorting by it would quietly re-sort the list by a different fact.
      `data-order` is the index Liquid emitted, which is the only sort key that cannot drift.
    */
    if (mode === 'shortest') {
      rows.sort((a, b) => {
        const d = Number(a.dataset.mins) - Number(b.dataset.mins);
        return d !== 0 ? d : Number(a.dataset.order) - Number(b.dataset.order);
      });
    } else if (mode === 'az') {
      rows.sort((a, b) => this.titleOf(a).localeCompare(this.titleOf(b), undefined, { sensitivity: 'base' }));
    } else {
      rows.sort((a, b) => Number(a.dataset.order) - Number(b.dataset.order));
    }

    /*
      ONE fragment, one reflow. Appending nine nodes one at a time to a live list is nine
      layout passes and it shows as a flicker on a phone.
    */
    const frag = document.createDocumentFragment();
    rows.forEach((row) => frag.appendChild(row));
    this.list.appendChild(frag);
    this.markTail();
  }

  /*
    THE TAIL OF THE VISIBLE LIST, NOT OF THE DOM (r4, 2026-09-12). An odd number of cards in a
    two-column grid leaves a hole beside the last one, and on the phone the answer is to let
    that card span both columns (`component-shintop-guide-index.css`, the 360-449 band, where
    the span is measured to cost nothing). CSS can say "last child in an odd position"; it
    cannot say "last card still visible", and `item.hidden` leaves the row in the DOM — so
    after a search returning five of nine the DOM's last child is a HIDDEN row and the
    nth-child rule fires on nothing while the fifth visible card orphans. Hence the class.

    With this file never loaded, nothing has been filtered or re-ordered, so the nth-child
    rule is exactly right — which is why the stylesheet gates it on `:not(.is-managed)`
    rather than replacing it.
  */
  markTail() {
    const shown = this.items.filter((item) => !item.hidden);
    this.items.forEach((item) => item.classList.remove('is-tail'));
    if (shown.length % 2 === 1) shown[shown.length - 1].classList.add('is-tail');
  }

  titleOf(row) {
    const el = row.querySelector('.shintop-guides__title');
    return el ? el.textContent.trim() : '';
  }

  print(count, searching) {
    if (!this.tally || !this.tallyTemplates) return;
    const key = searching ? 'found' : 'all';
    const form = count === 1 ? 'One' : 'Other';
    const template = this.tallyTemplates[key + form];
    if (!template) return;
    this.tally.textContent = template.replace('%%N%%', String(count));
  }
}

if (!customElements.get('shintop-guide-finder')) {
  customElements.define('shintop-guide-finder', ShintopGuideFinder);
}
